import Link from 'next/link';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import { Colors, Breakpoints } from '../../shared/styles/StyleConst';
import { YadoDetailJson } from '../../types/response_types/YadoResponseTypes';
import Price from './Price';
import { createOnsenYadoDetailLink } from '../../shared/utils/url_helper';
import i18n from '../../R/string/i18n';
import ImageLazy from '../common/ImageLazy';
import ExchangeRate from '../../models/core/ExchangeRate';
import { evaluationLabel } from '../../helpers/yado_helper';
import YadoPrice from '../../models/mobx/YadoPrice';
import OnsenYadoSearchResultPlaceholder from './OnsenYadoSearchResultPlaceholder';
import SearchCondition from '../../models/search/SearchCondition';
import getYadoPopularServices, {
  PopularServices,
  getServices,
} from '../../helpers/popular_services_helper';
import FacilityLabel from './FacilityLabel';
import YadoPhotoGalleryNoThumbs from '../yados/YadoPhotoGalleryNoThumbs';
import PriceJpy from './PriceJpy';
import { DeviceContext } from '../../models/context/DeviceContext';
import WAmazingLimitedLabel from '../yados/WAmazingLimitedLabel';

type Props = {
  yado: YadoDetailJson;
  featureArticleImage?: string | Null;
  yadoPrice: YadoPrice | Null;
  isLoadingOverAll?: boolean;
  searchedCondition: SearchCondition;
  utmParams?: { [key: string]: string } | Null;
  handleClick?: (event: any) => void;
  userExchangeRate?: ExchangeRate | Null;
};

export const SoldOut = () => (
  <div className="sold-out-container d-flex flex-column justify-content-end align-items-end">
    <div className="exclamation d-flex justify-content-center align-items-center">!</div>
    <div className="sold-out-text">{i18n.webYadoRoomsSoldOutOnSelectedDate()}</div>
    <div className="other-date">{i18n.webYadoCheckOtherDate()}</div>
    <style jsx>
      {`
        .sold-out-container {
          font-size: 0.75rem;
          padding-top: 5px;
          white-space: nowrap;
          width: auto;
        }
        .exclamation {
          color: #2187f2;
          font-size: 14px;
          font-weight: normal;
          height: 18px;
          width: 18px;
          border-radius: 50%;
          border: solid 1px #2187f2;
        }
        .sold-out-text {
          margin-top: 5px;
          white-space: pre-wrap;
        }
        .other-date {
          color: ${Colors.textDarkGray};
          font-weight: 700;
          margin-top: 5px;
        }
        @media screen and (min-width: ${Breakpoints.lgMin}) {
          .sold-out-text {
            padding-left: 40px;
          }
          .exclamation {
            font-weight: bold;
            border-width: 2px;
          }
        }
      `}
    </style>
  </div>
);

const PRICE_AREA_WIDTH = 160;

const OnsenYadoSearchResult = ({
  isLoadingOverAll = false,
  yado,
  featureArticleImage,
  yadoPrice,
  searchedCondition,
  handleClick,
  userExchangeRate,
  utmParams,
}: Props) => {
  const { isMobile } = useContext(DeviceContext);
  const priceIncludingTax = yadoPrice?.priceIncludingTax;
  const adultCount = searchedCondition.totalAdults;
  const childCount = searchedCondition.totalChildren;
  const currencyName = userExchangeRate?.currency;
  const popularServices = getYadoPopularServices(yado);
  const availableServices = (Object.keys(popularServices) as Array<keyof PopularServices>).filter(
    service => popularServices[service],
  );
  const services = getServices(true);

  const yadoLinkWithSearchCondition = createOnsenYadoDetailLink({
    yadoId: yado.id,
    searchCondition: searchedCondition,
    utmParams,
  });
  const yadoLinkWithoutSearchCondition = createOnsenYadoDetailLink({ yadoId: yado.id, utmParams });
  const yadoLink = yadoPrice?.isSoldOut
    ? yadoLinkWithoutSearchCondition
    : yadoLinkWithSearchCondition;

  const featuredImage = featureArticleImage || null;
  const yadoImages = yado.images.slice(0, 5);

  return (
    <>
      <div className="wrapper">
        <div className="images-container">
          <div className="yado-image-container">
            {featuredImage ? (
              <ImageLazy src={featuredImage} alt="yado" />
            ) : (
              <YadoPhotoGalleryNoThumbs
                images={yadoImages}
                imageSizes="(max-width:992px) 100%, 300px"
              />
            )}
          </div>
        </div>
        <Link className="link-wrapper" {...yadoLink}>
          <button onClick={handleClick} type="button">
            <div className="yado-info-container row no-gutters justify-content-between">
              <div className="top-content">
                <div className="yado-left-container">
                  <div className="name-wrapper">
                    <h2 className="name mb-0">{yado.localizedName}</h2>
                  </div>
                  <div className="rate-wrapper d-flex">
                    {yado.rating.total && (
                      <div className="rate">
                        <span
                          style={{
                            fontWeight: 'bold',
                            marginRight: '10px',
                          }}
                        >
                          {yado.rating.total}/10
                        </span>
                        {evaluationLabel(Number(yado.rating.total))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="yado-right-container">
                  <div className="text-right">
                    <div className="price-wrapper">
                      {priceIncludingTax != null && (
                        <>
                          {yadoPrice?.discountedPriceIncludingTax != null && isMobile.phone && (
                            <div
                              className="justify-content-start discount-price-mobile"
                              style={{ marginBottom: '5px' }}
                            >
                              <div className="d-flex justify-content-end">
                                <p className="coupon-text mb-1">
                                  {i18n.webYadoCouponAppliedText()}
                                  {'\u00A0'}
                                </p>
                                <Price
                                  priceJpy={priceIncludingTax}
                                  exchange
                                  size="xs"
                                  showCurrencyName={false}
                                  strikeThrough
                                  strikeThroughLineColor="#545454"
                                />
                                <span className="discount-currency">{`${'\u00A0'}${currencyName}`}</span>
                              </div>
                            </div>
                          )}
                          <PriceJpy
                            price={
                              yadoPrice?.discountedPriceIncludingTax == null
                                ? priceIncludingTax
                                : yadoPrice?.discountedPriceIncludingTax
                            }
                          />
                          <div className="price-parallel">
                            {yadoPrice?.discountedPriceIncludingTax != null && !isMobile.phone && (
                              <div className="justify-content-start discount-price">
                                <p className="coupon-text mb-1">
                                  {i18n.webYadoCouponAppliedText()}
                                </p>
                                <div className="d-flex justify-content-end">
                                  <Price
                                    priceJpy={priceIncludingTax}
                                    exchange
                                    size="xs"
                                    showCurrencyName={false}
                                    strikeThrough
                                    strikeThroughLineColor="#545454"
                                  />
                                  <span className="discount-currency">{`${'\u00A0'}${currencyName}`}</span>
                                </div>
                              </div>
                            )}
                            <div className="d-flex justify-content-end">
                              <Price
                                priceJpy={
                                  yadoPrice?.discountedPriceIncludingTax == null
                                    ? priceIncludingTax
                                    : yadoPrice?.discountedPriceIncludingTax
                                }
                                exchange
                                size={isMobile.phone ? 'md' : 'ml'}
                                showCurrencyName={false}
                                fontWeight="bold"
                              />
                              <div className="price-info d-flex">{`${currencyName}\n${i18n.webYadoTaxIncluded()}`}</div>
                            </div>
                          </div>
                          <div className="black-text mt-1 mb-2">
                            {i18n.yadoAdultWithNumLabel({ count: adultCount })}
                            {childCount >= 1 &&
                              `${i18n.webYadoDelimiter()}${i18n.yadoChildrenWithNumLabel({
                                count: childCount,
                              })}`}
                            <br />
                            {i18n.webYadoLowestPriceForOneDay()}
                          </div>
                        </>
                      )}
                      {yadoPrice?.isSoldOut && (
                        <div>
                          <SoldOut />
                        </div>
                      )}
                      {yadoPrice?.isLoading && <div>Loading...</div>}
                    </div>
                    <div className="d-none">
                      {yadoPrice?.isSoldOut && <span>{i18n.webYadoCheckOtherDate()}</span>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="services">
                <WAmazingLimitedLabel limited={yado.hasWamazingExclusivePlan} />
                {availableServices.map(service => {
                  const { text, icon } = services[service];
                  const displayText = text();

                  return <FacilityLabel key={displayText} text={displayText} icon={icon} />;
                })}
              </div>
              <p className="catchphrase d-none d-lg-flex mb-0">{yado.catchphrase}</p>
            </div>
            {isLoadingOverAll && <OnsenYadoSearchResultPlaceholder />}
          </button>
        </Link>
      </div>
      <style jsx>{`
        .yado-right-container :global(.coupon-label) {
          font-size: 0.6875rem;
          font-weight: 500;
          margin-top: 2px;
        }
        .wrapper {
          position: relative;
          background-color: ${Colors.onsenWhite};
          transition: all ease-in 0.2s;
        }
        .wrapper:hover {
          box-shadow: 0 0 8px #cecece;
        }
        .link-wrapper {
          color: ${Colors.textDarkGray};
          text-decoration: none;
          display: block;
          width: 100%;
        }

        :global(.link-wrapper button) {
          background-color: transparent;
          border: none;
          width: 100%;
        }

        :global(.link-wrapper button:focus) {
          outline: none;
        }

        .black-text {
          color: ${Colors.textDarkGray};
          font-size: 12px;
          white-space: pre-wrap;
        }
        .yado-info-container {
          color: ${Colors.textDarkGray};
          height: auto;
          padding: 7px;
        }
        .info-wrapper {
          margin: 36px 10px 24px;
          display: flex;
          justify-content: space-around;
          align-items: center;
        }
        .name-wrapper {
          align-items: center;
          display: flex;
          margin-bottom: 12px;
          flex-wrap: wrap;
          text-align: left;
        }
        .name {
          font-size: 1rem;
          font-weight: 700;
        }
        .rate-wrapper {
          font-size: 13px;
        }
        .rate {
          margin-right: 10px;
        }
        .catchphrase {
          margin-top: 10px;
          font-size: 15px;
          color: ${Colors.textDarkGray};
          letter-spacing: 1px;
          text-align: left;
        }
        .price-info {
          white-space: pre-wrap;
          font-size: 10px;
          font-weight: bold;
          line-height: 12px;
          padding-left: 5px;
          color: ${Colors.textDarkGray};
        }
        .coupon-text {
          color: ${Colors.onsenRed};
          font-size: 13px;
          line-height: 16px;
          text-align: right;
          width: 100px;
        }
        .discount-currency {
          color: ${Colors.onsenGray800};
          font-size: 12px;
          font-weight: bold;
          text-decoration: line-through solid;
          line-height: 17px;
        }
        .label-container {
          width: 100%;
        }
        .images-container {
          height: 252px;
        }
        .yado-image-container {
          width: 100%;
          height: 252px;
          position: relative;
        }
        .yado-image-container :global(img) {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .rating {
          width: auto;
        }
        .location {
          color: ${Colors.textDarkGray};
          font-size: 12px;
        }
        .rating-number {
          font-weight: 600;
          font-size: 18px;
          color: ${Colors.onsenPink};
          letter-spacing: 0;
          text-align: center;
        }
        .coupon-label {
          font-size: 8px;
          margin-right: 5px;
          line-height: 8px;
          color: ${Colors.onsenRed};
        }
        .yado-left-container {
          flex: 1 1 50%;
          width: 100%;
        }
        .yado-right-container {
          flex: 1;
          width: 100%;
        }
        .services {
          display: flex;
          flex-wrap: wrap;
          margin-top: 10px;
          padding-top: 8px;
          border-top: 1px solid ${Colors.gray300};
          width: 100%;
        }
        .services :global(.facility-label-container) {
          margin: 0 8px 5px 0;
        }
        .yado-info-container .top-content {
          display: flex;
          width: 100%;
        }
        @media screen and (min-width: ${Breakpoints.lgMin}) {
          .wrapper {
            display: flex;
          }
          .images-container {
            height: auto;
            min-height: 275px;
          }
          .yado-image-container {
            width: 300px;
            min-height: 275px;
            height: 100%;
            position: relative;
          }
          .yado-info-container {
            padding: 20px;
          }
          .name {
            font-size: 1.0625rem;
          }
          .price-wrapper {
            margin: 10px 0;
          }
          .yado-left-container {
            max-width: calc(100% - ${PRICE_AREA_WIDTH}px);
          }
          .yado-right-container {
            min-width: ${PRICE_AREA_WIDTH}px;
            flex: 0;
          }
          .rate-wrapper {
            font-size: 14px;
          }
          .price-parallel {
            display: flex;
            justify-content: flex-end;
            gap: 20px;
          }
          .discount-price {
            position: relative;
            top: -14px;
          }
        }
      `}</style>
    </>
  );
};

export default observer(OnsenYadoSearchResult);
